import { ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { Component, computed, inject, input, InputSignal, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import { combineLatest, defer, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, take, takeUntil, tap } from 'rxjs/operators';

import { Store } from '@ngxs/store';

import {
  DATA_SELECTOR_INJECTION_TOKEN,
  downloadFileFromUrl,
  Loadable,
  Logger,
  SnackbarService,
  SnackbarType,
  UploadFileJob,
} from '@aiwyn/shared';
import { FilePreviewService } from '@aiwyn/services';
import { BaseCommonClientRequest, ClientRequestIdentityHelper } from '@aiwyn/common/client-requests';
import { ClientRequestDocumentUploadModel, RequestDocument } from '@aiwyn/api';

import { DmsIntegrationApiService, EntityType, ExtendedFile, UploadType } from 'src/app/shared/components/upload-files';
import { ClientRequestsState, ReplaceFileForClientRequest, UndoFileCategorization } from 'src/app/features/client-requests/state';
import { FileNameConfirmationDialogComponent, FileNameConfirmationDialogData } from 'src/app/components/main/modals/file-name-confirmation-dialog/file-name-confirmation-dialog.component';
import { FilesLinkModalComponent, FilesLinkModalData } from 'src/app/features/client-requests/components';
import { ClientRequestDocumentFileDisplay } from 'src/app/features/client-requests/models';

import { FileDisplay } from './file-item.model';
import { buildFileItemPreviewList } from './file-item.utils';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'awn-file-item',
  templateUrl: './file-item.component.html',
  styleUrl: './file-item.component.scss',
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'file-item',
    },
  ],
  standalone: false
})
export class FileItemComponent implements OnDestroy {
  file: InputSignal<FileDisplay> = input()
  fileIndex: InputSignal<number> = input()
  siblingFiles: InputSignal<FileDisplay[]> = input<FileDisplay[]>()
  documentItem: InputSignal<RequestDocument> = input()
  isEditingAllowed: InputSignal<boolean> = input()
  instance: InputSignal<BaseCommonClientRequest<ClientRequestDocumentUploadModel>> = input();

  private _dmsIntegrationApiService = inject(DmsIntegrationApiService)
  private _launchDarklyService = inject(LaunchDarklyService)
  private _filePreviewService = inject(FilePreviewService)
  private _snackbarService = inject(SnackbarService)
  private _configService = inject(ConfigService)
  private _dialog = inject(MatDialog)
  private _logger = inject(Logger)
  private _store = inject(Store)
  private _date = inject(DatePipe)

  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  // Upload component constants
  protected readonly EntityType = EntityType;
  protected readonly UploadType = UploadType;

  acceptedFileTypes: string[] = [];

  public isLoading = signal<boolean>(false)
  public replacementFileLoading = signal<boolean>(false)

  public fileMetaText = computed(() => {
    if (this.file().createdByContact?.contact_info?.full_name && this.file().createdAt) {
      return `Uploaded by ${this.file().createdByContact.contact_info.full_name} • ${this._date.transform(this.file().createdAt, 'MMM d, y h:mm a')}`
    }

    if (this.file().createdByContact?.contact_info?.full_name) {
      return `Uploaded by ${this.file().createdByContact.contact_info.full_name}`
    }

    if (this.file().createdAt) {
      return `Uploaded on ${this._date.transform(this.file().createdAt, 'MMM d, y h:mm a')}`
    }
  });

  public isFileFlaggingEnabled = toSignal(
    this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_FILE_FLAGGING),
    { initialValue: false }
  );

  jobId$ = defer(() => this.instance().groupIdentity$).pipe(
    map(identity => ClientRequestIdentityHelper.parseGroupIdentity(identity)),
    map(parsedIdentity => parsedIdentity.id),
  );

  protected readonly _triggerFileSelection$ = new Subject<void>();
  selectedFileForReplacement: FileDisplay | null = null;

  allClientRequestFileDisplay$: Observable<ClientRequestDocumentFileDisplay[]> = combineLatest([
    this._store.select(ClientRequestsState.getUploadedFileDisplays),
    this._store.select(ClientRequestsState.getCategorizedFileDisplays),
  ]).pipe(
    map(([uncategorizedFiles, categorizedFiles]) => {
      return [...uncategorizedFiles, ...categorizedFiles];
    }),
  );

  private _destroy$ = new Subject<void>()

  constructor() {
    this.acceptedFileTypes = this._configService.clientRequestFileTypeWhitelist;
  }

  replaceFile(file: FileDisplay, event: Event): void {
    event.stopPropagation();
    this.selectedFileForReplacement = file;
    this._triggerFileSelection$.next();
  }

  previewFile(file: FileDisplay, e?: MouseEvent): void {
    if (file.isMasked) {
      return;
    }

    e?.stopPropagation();

    const { files, initialIndex } = buildFileItemPreviewList(this.siblingFiles(), file);
    this._filePreviewService.previewMany(files, initialIndex);
  }

  downloadFile(file: FileDisplay, e?: MouseEvent): void {
    e?.preventDefault()
    e?.stopPropagation()

    this.isLoading[file.id] = true

    this._dmsIntegrationApiService.getDownloadUrl({ files: [file.id], isMultipleDownload: false }).pipe(
      tap(url => {
        downloadFileFromUrl({ href: url, download: `${file.name}.${file.extension}`, target: '_blank' });
        this.isLoading[file.id] = false
      }),
      catchError((err) => {
        this._snackbarService.open(
          'It seems that there is an error in this file, please try again or contact support',
          SnackbarType.ERROR
        );

        return err
      }),
      takeUntil(this._destroy$)
    ).subscribe()
  }


  onReplaceFileUploadStart(files: Loadable<ExtendedFile>[]): void {
    const originalFile = this.selectedFileForReplacement;
    if (originalFile?.id) {
      this.replacementFileLoading.set(true);
    }
  }

  onReplaceFileUploadComplete(files: UploadFileJob[]): void {
    if (!this.selectedFileForReplacement || files.length !== 1) {
      console.error('Invalid replacement upload:', { selectedFile: this.selectedFileForReplacement, filesCount: files.length });
      // Clear loading state even for invalid uploads
      if (this.selectedFileForReplacement?.id) {
        this.replacementFileLoading.set(false);
      }
      return;
    }

    const uploadedFile = files[0];
    const originalFile = this.selectedFileForReplacement;

    if (!uploadedFile.identity) {
      console.error('Uploaded file missing identity (DMS ID)');
      this._snackbarService.open(
        'Failed to upload replacement file - missing DMS ID',
        SnackbarType.ERROR
      );
      // Clear loading state for missing DMS ID
      if (originalFile?.id) {
        this.replacementFileLoading.set(false);
      }
      this.selectedFileForReplacement = null;
      return;
    }

    // TODO remove this once the new logic is implemented
    // Check if file names are different and show confirmation if needed
    const uploadedFileName = this.getFileNameWithoutExtension(uploadedFile.data.file.name);
    const originalFileName = originalFile.name;

    this.proceedWithFileReplacement(originalFile, uploadedFile);

    // TODO replace this logic with the new stream API that requires this confirmation in the upload-file.component
    // if (uploadedFileName.toLowerCase() !== originalFileName.toLowerCase()) {
    //   this.showFileNameConfirmationDialog(originalFile.name, originalFile.extension)
    //     .pipe(takeUntil(this._destroy$))
    //     .subscribe(confirmed => {
    //       if (confirmed) {
    //         this.proceedWithFileReplacement(originalFile, uploadedFile);
    //       } else {
    //         // User cancelled, clear loading state
    //         if (originalFile?.id) {
    //           this.replacementFileLoading.set(false);
    //         }
    //         this.selectedFileForReplacement = null;
    //       }
    //     });
    // } else {
    //   // Names match, proceed directly
    //   this.proceedWithFileReplacement(originalFile, uploadedFile);
    // }
  }

  private showFileNameConfirmationDialog(originalFileName: string, originalExtension: string): Observable<boolean> {
    const dialogRef = this._dialog.open(FileNameConfirmationDialogComponent, {
      width: '500px',
      data: {
        originalFileName,
        originalExtension
      } as FileNameConfirmationDialogData
    });

    return dialogRef.afterClosed().pipe(
      map(result => !!result)
    );
  }

  private getFileNameWithoutExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  }

  private proceedWithFileReplacement(originalFile: FileDisplay, uploadedFile: UploadFileJob): void {
    // Dispatch the new action with individual arguments for better traceability
    this._store.dispatch(new ReplaceFileForClientRequest(
      this.documentItem().id,           // clientRequestItemId
      originalFile.id,                  // originalFileId
      uploadedFile.data?.fileExternalId // externalDmsFileId
    )).pipe(
      tap(() => {
        this._snackbarService.open(
          `File ${originalFile.name}.${originalFile.extension} replaced successfully`,
          SnackbarType.SUCCESS
        );
      }),
      catchError(error => {
        this._logger.error('DocumentItemContentComponent :: onReplaceFileUploadComplete :: Error while replacing file.', error);
        this._snackbarService.open(
          `Failed to replace file ${originalFile.name}.${originalFile.extension}`,
          SnackbarType.ERROR
        );
        return of(null);
      }),
      finalize(() => {
        // Clear loading state and selected file regardless of success/failure
        if (originalFile?.id) {
          this.replacementFileLoading.set(false);
        }
        this.selectedFileForReplacement = null;
      }),
      takeUntil(this._destroy$)
    ).subscribe();
  }


  linkToRequests(file: FileDisplay): void {
    this.allClientRequestFileDisplay$.pipe(
      take(1),
      tap((clientRequestFilesList) => {
        const selectedFile = clientRequestFilesList.find((innerFile) => {
          return innerFile.id === file.id;
        });

        this._dialog.open<FilesLinkModalComponent, FilesLinkModalData>(
          FilesLinkModalComponent,
          {
            data: {
              clientRequestId: this._store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId),
              file: selectedFile,
              instance: this.instance(),
              notApplicableItemIds: new Set(this._store.selectSnapshot(ClientRequestsState.getNotApplicableItemsForSelectedRequest)),
            },
          },
        );
      }),
      takeUntil(this._destroy$),
    ).subscribe();

  }

  unlinkFile(file: FileDisplay): void {
    const clientRequestId = this._store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);
    const fileName = `${file.name}.${file.extension}`;

    this._store.dispatch(new UndoFileCategorization({
      clientRequestId,
      clientRequestDocumentFileCategorizeList: [
        {
          fileId: file.id,
          documentId: this.documentItem().id,
          fileName,
        },
      ],
    }));
  }

  ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()
    this._triggerFileSelection$.complete()
  }
}
